import type { ApiSongListResponse, ApiSongDetailResponse } from '../types/song';
import type { PlayHistoryResponse } from '../types/playHistory';
import type { BingoApiResponse, BingoListItem, SongTitle } from '../types/bingo';
import type { LoginRequest, LoginResponse, RegisterRequest, RegisterResponse, LegacyLoginResponse, UpdateUserRequest, UpdateUserResponse, UserProfileResponse, ScoreThreshold, KonasutePackInfo } from '../types/auth';
import type { ExScoreThreshold } from '../types/scoreThreshold';
import type { RecordListParams, RecordListResponse } from '../types/record';
import type { VolforceApiResponse, VolforcePotentialResponse } from '../types/volforce';
import type { DiffTableListItem, DiffTableDetailResponse } from '../types/diffTable';
import type { RivalListResponse, RivalListParams, RivalRecordsResponse, RivalRecordsBatchPair, RivalRecordsBatchResponse, RivalComparisonResponse, RivalFollower, RivalSimple, RivalTrendResponse } from '../types/rival';
import type { PlaylistListItem, PlaylistDetailResponse, CreatePlaylistRequest, UpdatePlaylistRequest, CreatePlaylistResponse } from '../types/playlist';
import type { GameVersion } from '../contexts/GameVersionContext';
import type { KonasutePlayLogPage, KonasuteHistoryParams, LevelTrendResponse, RecentScoresResponse } from '../types/konasuteLog';
import type { SynonymListResponse, SynonymDto, AddSynonymRequest, BatchAddSynonymRequest, BatchSynonymResponse, DeleteSynonymResponse, CacheRefreshResponse, CacheStatsResponse, ArtistSynonymListResponse, ArtistSynonymDto, AddArtistSynonymRequest, ArtistLinkListResponse, ArtistLinkDto, AddArtistLinkRequest, BatchAddArtistLinkRequest, BatchArtistLinkResponse, UpdateSongDifficultyRequest, UpdateSongDifficultyResponse } from '../types/admin';
import type { DashboardSummaryResponse, RecentHistoryResponse, PlayDateHistory, MostPlayedResponse } from '../types/dashboard';
import type { SongComment } from '../types/comment';
import type { SongRecommendRequest, SongRecommendResponse, CanUseMedianResponse } from '../types/recommend';
import type { AnalysisResponse, AnalysisStatus } from '../types/analysis';
import type { Tag, TagWithIntensity, TagUpsertRequest, TagAssignmentDto } from '../types/tag';
import type { TagAnalysisResponse, TagRankingResponse } from '../types/tagAnalysis';
import type { ScoreTrendAxis, ScoreTrendResponse } from '../types/scoreTrend';
import type { SimilarChartsResponse } from '../types/similarCharts';
import type { NotificationDto, PushDeviceDto, PushSubscribePayload } from '../types/notification';
import type { ProfileAsset, AssetKind, ProfileCustomizationRequest, ProfileAssetUpdateRequest, AssetGrantResult } from '../types/profileAsset';
import type { VgcState, VgcMatch, VgcShopItem, VgcWallet, VgcSide, VgcMatchVotes, VgcPredictionStats } from '../types/vgc';
import type {
  AchievementPeekItem,
  AchievementPeekResponse,
  AchievementSurface,
  Achievement,
  AchievementSummary,
  BackfillResult,
  ProgressPage,
  OutboxEntry,
  OutboxPage,
  OutboxStatus,
  UnlockedAchievementItem,
  UnlockedAchievementListResponse,
  ManageUserSearchItem,
} from '../types/achievement';
import type { RedeemResult, AdminRedeemCode, UpsertRedeemCodeRequest } from '../types/redeem';
import type {
  KonasuteHookConfig,
  KonasuteHookStatus,
  KonasuteHookGrantedUser,
  KonasuteHookGrantResult,
} from '../types/konasuteHook';
import type { PlayCountEntryUpdate, PlayCountHistory } from '../types/playCountHistory';

// Import rival types from separate file

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';

// In-flight request cache to prevent duplicate API calls (e.g., from React StrictMode)
const inFlightRequests = new Map<string, Promise<unknown>>();

function dedupeRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
  const existing = inFlightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = requestFn().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, promise);
  return promise;
}

// Helper to get versioned base URL
const getVersionedBaseUrl = (version: GameVersion = '6'): string => {
  return `${API_BASE_URL}/${version}`;
};

// Helper to get current game version from localStorage
const getCurrentVersion = (): GameVersion => {
  const saved = localStorage.getItem('gameVersion');
  return (saved === '6' || saved === '7') ? saved : '7';
};

export interface ClearTypesResponse {
  records: Array<{
    songId: number;
    difficulty: string; // backend enum name: NOVICE | ADVANCED | ...
    clearType: 'PLAYED' | 'CLEAR' | 'EX' | 'MXV' | 'UC' | 'PUC';
  }>;
}

export interface SongListParams {
  query?: string;
  lastSongId?: number;
  diffNum?: number;
  levelLow?: number;
  levelHigh?: number;
  sortType?: string;
  sortOrder?: string;
  excludeEac?: boolean;
  tagIds?: string[];
}

export interface SongDetailParams {
  songIdx: number;
  difficulty: string;
}

export interface RecordDetailParams {
  songIdx: number;
  diff: string;
}

class ApiService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Send preview UI language as Accept-Language so future backend i18n
    // can negotiate without an additional roundtrip. Falls back to ko.
    try {
      const lang = localStorage.getItem('preview.lang');
      if (lang === 'ko' || lang === 'en' || lang === 'ja') {
        headers['Accept-Language'] = `${lang},ko;q=0.5`;
      }
    } catch {
      /* ignore privacy-mode storage failures */
    }

    return headers;
  }

  // Inspect response headers for achievement unlock signal and dispatch event if needed.
  private checkAchievementHeader(response: Response): void {
    const pending = response.headers.get('x-achievement-unlock-pending');
    if (pending && Number(pending) > 0) {
      window.dispatchEvent(new CustomEvent('achievement:pending'));
    }
  }

  // Common (non-versioned) API call
  private async fetchApi<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      });
    }

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      // Clear invalid auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = '/login';
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Versioned API call - uses game version in path
  private async fetchVersionedApi<T>(endpoint: string, params?: Record<string, any>, version?: GameVersion): Promise<T> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const url = new URL(`${baseUrl}${endpoint}`, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      });
    }

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      // Clear invalid auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      const currentVersion = getCurrentVersion();
      window.location.href = `/${currentVersion}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      // Parse error response to get specific error code
      try {
        const errorData = await response.json();
        throw new Error(errorData.code || 'ACCESS_DENIED');
      } catch (e) {
        if (e instanceof Error && e.message !== 'ACCESS_DENIED') {
          throw e;
        }
        throw new Error('ACCESS_DENIED');
      }
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Versioned API call with array params (repeated query params, e.g. ?tagIds=a&tagIds=b)
  private async fetchVersionedApiWithArrayParams<T>(
    endpoint: string,
    params?: Record<string, any>,
    arrayParams?: Record<string, string[] | undefined>,
    version?: GameVersion
  ): Promise<T> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const url = new URL(`${baseUrl}${endpoint}`, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      });
    }

    if (arrayParams) {
      Object.entries(arrayParams).forEach(([key, values]) => {
        if (values && values.length > 0) {
          values.forEach((val) => url.searchParams.append(key, val));
        }
      });
    }

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      const currentVersion = getCurrentVersion();
      window.location.href = `/${currentVersion}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      try {
        const errorData = await response.json();
        throw new Error(errorData.code || 'ACCESS_DENIED');
      } catch (e) {
        if (e instanceof Error && e.message !== 'ACCESS_DENIED') {
          throw e;
        }
        throw new Error('ACCESS_DENIED');
      }
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // === Song endpoints (versioned) ===
  async getSongList(params: SongListParams, version?: GameVersion): Promise<ApiSongListResponse[]> {
    const v = version || getCurrentVersion();
    const key = `song-list:${v}:${JSON.stringify(params)}`;
    const { tagIds, ...rest } = params;
    return dedupeRequest(key, () =>
      this.fetchVersionedApiWithArrayParams<ApiSongListResponse[]>('/song/list', rest, { tagIds }, version)
    );
  }

  async getSongListCount(
    params: Omit<SongListParams, 'lastSongId' | 'sortType' | 'sortOrder'>,
    version?: GameVersion
  ): Promise<number> {
    const v = version || getCurrentVersion();
    const key = `song-list-count:${v}:${JSON.stringify(params)}`;
    const { tagIds, ...rest } = params;
    const result = await dedupeRequest(key, () =>
      this.fetchVersionedApiWithArrayParams<{ totalCount: number }>('/song/list/count', rest, { tagIds }, version)
    );
    return result.totalCount;
  }

  async getSongDetail(params: SongDetailParams, version?: GameVersion): Promise<ApiSongDetailResponse> {
    const v = version || getCurrentVersion();
    const key = `song-detail:${v}:${params.songIdx}:${params.difficulty}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<ApiSongDetailResponse>('/song/detail', params, version)
    );
  }

  async getSingleSong(songIdx: number, version?: GameVersion): Promise<ApiSongListResponse> {
    const v = version || getCurrentVersion();
    const key = `song-single:${v}:${songIdx}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<ApiSongListResponse>('/song/single', { songIdx }, version)
    );
  }

  async getSongTitles(version?: GameVersion): Promise<SongTitle[]> {
    return this.fetchVersionedApi<SongTitle[]>('/song/titles', undefined, version);
  }

  async getChartCountByLevel(version?: GameVersion): Promise<Record<number, number>> {
    return this.fetchVersionedApi<Record<number, number>>('/song/chart-count', undefined, version);
  }

  // === Song Bookmark endpoints (versioned) ===
  async getBookmarkedSongIds(version?: GameVersion): Promise<number[]> {
    const v = version || getCurrentVersion();
    const key = `song-bookmarks:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<number[]>('/song/bookmarks', undefined, version)
    );
  }

  async getBookmarkedSongsData(params: SongListParams, version?: GameVersion): Promise<ApiSongListResponse[]> {
    const v = version || getCurrentVersion();
    const key = `song-bookmarks-data:${v}:${JSON.stringify(params)}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<ApiSongListResponse[]>('/song/bookmarks/songs', params, version)
    );
  }

  async bookmarkSong(songId: number, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/song/${songId}/bookmark`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to bookmark song: ${response.status} ${response.statusText}`);
    }
  }

  async unbookmarkSong(songId: number, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/song/${songId}/bookmark`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to unbookmark song: ${response.status} ${response.statusText}`);
    }
  }

  // === Dashboard endpoints (versioned) ===
  async getDashboardSummary(version?: GameVersion): Promise<DashboardSummaryResponse> {
    const v = version || getCurrentVersion();
    const key = `dashboard-summary:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<DashboardSummaryResponse>('/dashboard/summary', undefined, version)
    );
  }

  async getPublicDashboardSummary(identifier: string, version?: GameVersion): Promise<DashboardSummaryResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/dashboard/public/${encodeURIComponent(identifier)}/summary`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async getPublicRecentHistory(identifier: string, version?: GameVersion): Promise<RecentHistoryResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/dashboard/public/${encodeURIComponent(identifier)}/recent-history`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async getPublicMostPlayedSongs(identifier: string, limit?: number, version?: GameVersion): Promise<MostPlayedResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const url = new URL(`${baseUrl}/dashboard/public/${encodeURIComponent(identifier)}/most-played`, window.location.origin);
    url.searchParams.append('limit', (limit || 30).toString());
    const response = await fetch(url.toString(), { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async getRecentHistory(version?: GameVersion, days?: number): Promise<RecentHistoryResponse> {
    const v = version || getCurrentVersion();
    const d = days ?? 7;
    const key = `dashboard-recent:${v}:${d}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<RecentHistoryResponse>('/dashboard/recent-history', { days: d }, version)
    );
  }

  async getRecentHistoryByDateRange(
    startDate: string,
    endDate: string,
    version?: GameVersion,
    levelMin?: number,
    levelMax?: number,
    scoreMin?: number,
    scoreMax?: number,
    subTab?: 'score' | 'exScore',
    recordType?: 'ALL' | 'ARCADE' | 'KONASUTE'
  ): Promise<RecentHistoryResponse> {
    const params: Record<string, string | number> = { startDate, endDate };
    if (levelMin !== undefined) params.levelMin = levelMin;
    if (levelMax !== undefined) params.levelMax = levelMax;
    if (scoreMin !== undefined) params.scoreMin = scoreMin.toFixed(2);
    if (scoreMax !== undefined) params.scoreMax = scoreMax.toFixed(2);
    if (subTab !== undefined) params.subTab = subTab;
    if (recordType !== undefined) params.recordType = recordType;
    return this.fetchVersionedApi<RecentHistoryResponse>('/dashboard/recent-history-range', params, version);
  }

  async getMostPlayedSongs(limit?: number, version?: GameVersion): Promise<MostPlayedResponse> {
    const v = version || getCurrentVersion();
    const key = `dashboard-most-played:${v}:${limit || 30}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<MostPlayedResponse>('/dashboard/most-played', { limit: limit || 30 }, version)
    );
  }

  async exportRecentHistory(
    startDate: string,
    endDate: string,
    subTab: 'score' | 'exScore',
    version?: GameVersion,
    levelMin?: number,
    levelMax?: number,
    scoreMin?: number,
    scoreMax?: number,
    exScoreDisplayMode?: string,
    recordType?: 'ALL' | 'ARCADE' | 'KONASUTE'
  ): Promise<Blob> {
    const v = version || getCurrentVersion();
    const versionParam = v === '7' ? 'NABLA' : 'EXCEED_GEAR';
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams({
      startDate,
      endDate,
      version: versionParam,
      subTab,
    });
    if (levelMin !== undefined) params.append('levelMin', levelMin.toString());
    if (levelMax !== undefined) params.append('levelMax', levelMax.toString());
    if (scoreMin !== undefined) params.append('scoreMin', scoreMin.toFixed(2));
    if (scoreMax !== undefined) params.append('scoreMax', scoreMax.toFixed(2));
    if (exScoreDisplayMode) params.append('exScoreDisplayMode', exScoreDisplayMode);
    if (recordType) params.append('recordType', recordType);

    const response = await fetch(
      `${API_BASE_URL}/export/recentHistory?${params.toString()}`,
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      }
    );
    if (!response.ok) {
      throw new Error('Export failed');
    }
    return response.blob();
  }

  async searchSongsWithFilters(params: {
    query?: string;
    levelLow?: number;
    levelHigh?: number;
    diffNum?: number; // Bitmask for difficulty filter (default: 2047 = all difficulties)
  }, version?: GameVersion): Promise<ApiSongListResponse[]> {
    return this.fetchVersionedApi<ApiSongListResponse[]>('/song/list', {
      query: params.query,
      levelLow: params.levelLow ?? 1,
      levelHigh: params.levelHigh ?? 20,
      diffNum: params.diffNum ?? 2047, // All difficulties by default
      sortType: 'NAME',
      sortOrder: 'ASC',
      excludeEac: false,
    }, version);
  }

  // === Record endpoints (versioned) ===
  async addManualRecord(params: {
    songId: number;
    difficulty: string;
    score: number;
    exScore?: number;
    clearType: string;
    date?: string;
  }, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const response = await fetch(`${API_BASE_URL}/${v}/record/manual`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(params),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(await response.text());
  }

  async getClearTypes(
    songIds: number[],
    version?: GameVersion
  ): Promise<ClearTypesResponse> {
    const v = version || getCurrentVersion();
    const response = await fetch(`${API_BASE_URL}/${v}/record/clear-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ songIds }),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async deleteManualRecord(params: {
    songId: number;
    difficulty: string;
    date: string;
    score: number;
  }, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const response = await fetch(`${API_BASE_URL}/${v}/record/manual`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(params),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(await response.text());
  }

  async getRecordDetail(params: RecordDetailParams, version?: GameVersion): Promise<PlayHistoryResponse> {
    const v = version || getCurrentVersion();
    const key = `record-detail:${v}:${params.songIdx}:${params.diff}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<PlayHistoryResponse>('/record/detail', params, version)
    );
  }

  async getRecordList(params: RecordListParams, version?: GameVersion, calcVersion?: string): Promise<RecordListResponse> {
    const v = version || getCurrentVersion();
    const mergedParams = calcVersion ? { ...params, calcVersion } : params;
    const key = `record-list:${v}:${JSON.stringify(mergedParams)}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<RecordListResponse>('/record', mergedParams, version)
    );
  }

  // 공개 기록 조회 (인증 불필요)
  async getPublicRecordList(userId: string, params: Omit<RecordListParams, 'targetClass'>, version?: GameVersion): Promise<RecordListResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const url = new URL(`${baseUrl}/record/public/${encodeURIComponent(userId)}`, window.location.origin);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('사용자를 찾을 수 없습니다.');
      }
      if (response.status === 403) {
        throw new Error('ACCESS_DENIED');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // 공개 유저 프로필 조회
  async getPublicUserProfile(identifier: string, version?: GameVersion): Promise<{ displayName: string; userId: string; volforce: number | null; playCount: number | null; skillLevel: string | null; konasuteEnable: boolean; cardId: string; profileBackgroundId?: string | null; profileAvatarId?: string | null; profilePlateId?: string | null; backgroundAsset?: ProfileAsset | null; avatarAsset?: ProfileAsset | null; plateAsset?: ProfileAsset | null }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const url = new URL(`${baseUrl}/record/public/${encodeURIComponent(identifier)}/profile`, window.location.origin);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async exportVolforceImage(params: {
    recordType: 'ALL' | 'ARCADE' | 'KONASUTE';
    version: 'NABLA' | 'EXCEED_GEAR';
    viewMode: 'full' | 'compact' | 'dense';
    showPatternType: boolean;
    currentVolforce?: boolean;
    calcVersion?: 'NABLA' | 'EXCEED_GEAR';
    compactColumns?: 3 | 4;
    date?: string;
    imageFormat?: 'webp' | 'png';
    uiMode?: 'legacy' | 'preview';
    targetUserId?: string;
  }): Promise<Blob> {
    const url = new URL(`${API_BASE_URL}/export/volforce`, window.location.origin);
    url.searchParams.append('recordType', params.recordType);
    url.searchParams.append('version', params.version);
    url.searchParams.append('viewMode', params.viewMode);
    url.searchParams.append('showPatternType', String(params.showPatternType));
    if (params.currentVolforce) url.searchParams.append('currentVolforce', 'true');
    if (params.calcVersion) url.searchParams.append('calcVersion', params.calcVersion);
    if (params.compactColumns) url.searchParams.append('compactColumns', String(params.compactColumns));
    if (params.date) url.searchParams.append('date', params.date);
    if (params.imageFormat) url.searchParams.append('imageFormat', params.imageFormat);
    if (params.uiMode) url.searchParams.append('uiMode', params.uiMode);
    if (params.targetUserId) url.searchParams.append('targetUserId', params.targetUserId);
    const response = await fetch(url.toString(), { headers: this.getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }
    return response.blob();
  }

  async getVolforceTarget(recordType: 'ALL' | 'ARCADE' | 'KONASUTE' = 'ALL', version?: GameVersion, calcVersion?: string): Promise<VolforceApiResponse> {
    const v = version || getCurrentVersion();
    const key = `volforce-target:${v}:${recordType}:${calcVersion || ''}`;
    const params: Record<string, any> = { recordType };
    if (calcVersion) params.calcVersion = calcVersion;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<VolforceApiResponse>('/record/targetVolforce', params, version)
    );
  }

  // 공개 볼포스 조회 (인증 불필요)
  async getPublicVolforceTarget(userName: string, recordType: 'ALL' | 'ARCADE' | 'KONASUTE' = 'ALL', version?: GameVersion, calcVersion?: string): Promise<VolforceApiResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const url = new URL(`${baseUrl}/record/public/${encodeURIComponent(userName)}/targetVolforce`, window.location.origin);
    url.searchParams.append('recordType', recordType);
    if (calcVersion) url.searchParams.append('calcVersion', calcVersion);

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('사용자를 찾을 수 없습니다.');
      }
      if (response.status === 403) {
        throw new Error('ACCESS_DENIED');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // 현재 볼포스 표 조회 (v7 전용, v7에서 플레이한 곡만)
  async getCurrentVolforceTarget(recordType: 'ALL' | 'ARCADE' | 'KONASUTE' = 'ALL'): Promise<VolforceApiResponse> {
    const key = `current-volforce:${recordType}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<VolforceApiResponse>('/record/currentVolforce', { recordType }, '7')
    );
  }

  // 공개 현재 볼포스 표 조회 (v7 전용)
  async getPublicCurrentVolforceTarget(userName: string, recordType: 'ALL' | 'ARCADE' | 'KONASUTE' = 'ALL'): Promise<VolforceApiResponse> {
    const baseUrl = getVersionedBaseUrl('7');
    const url = new URL(`${baseUrl}/record/public/${encodeURIComponent(userName)}/currentVolforce`, window.location.origin);
    url.searchParams.append('recordType', recordType);

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('사용자를 찾을 수 없습니다.');
      }
      if (response.status === 403) {
        throw new Error('ACCESS_DENIED');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // 볼포스 퍼텐셜 조회 (v7 전용)
  async getVolforcePotential(recordType: 'ALL' | 'ARCADE' | 'KONASUTE' = 'ALL'): Promise<VolforcePotentialResponse> {
    const key = `volforce-potential:${recordType}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<VolforcePotentialResponse>('/record/volforcePotential', { recordType }, '7')
    );
  }

  // 특정 날짜 시점의 볼포스 표 조회 (v7 전용)
  async getVolforceTargetAtDate(recordType: 'ALL' | 'ARCADE' | 'KONASUTE', date: string, calcVersion?: string): Promise<VolforceApiResponse> {
    const key = `volforce-target-at-date:${recordType}:${date}:${calcVersion || ''}`;
    const params: Record<string, any> = { recordType, date };
    if (calcVersion) params.calcVersion = calcVersion;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<VolforceApiResponse>('/record/targetVolforceAtDate', params, '7')
    );
  }

  // 이전 버전 볼포스 조회 (v7 전용, v4/v5/v6 데이터 조회)
  async getPreviousVolforce(recordType: 'ALL' | 'ARCADE' | 'KONASUTE', previousVersion: 4 | 5 | 6): Promise<VolforceApiResponse> {
    const key = `previous-volforce:${recordType}:${previousVersion}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<VolforceApiResponse>('/record/previousVolforce', { recordType, previousVersion }, '7')
    );
  }

  // Play Date API - 플레이 이력이 있는 날짜 목록 조회 (versioned)
  async getPlayDates(identification: string, isId: boolean = true, version?: GameVersion): Promise<string[]> {
    return this.fetchVersionedApi<string[]>('/record/playDate', { identification, isId }, version);
  }

  // Play Date History API - 성장 그래프용 (volforce, playCount 포함)
  async getPlayDateHistory(version?: GameVersion): Promise<PlayDateHistory[]> {
    const v = version || getCurrentVersion();
    const key = `play-date-history:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<PlayDateHistory[]>('/record/playDateHistory', undefined, version)
    );
  }

  // === Bingo endpoints (versioned) ===
  async getBingo(bingoId: string, version?: GameVersion): Promise<BingoApiResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo/${bingoId}`, {
      headers: this.getAuthHeaders(),
    });

    if (response.status === 403) {
      throw new Error('비공개 빙고입니다.');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async getBingoList(opts?: { filter?: string; sort?: 'updated' | 'created' | 'bookmark' | 'name' } | GameVersion, version?: GameVersion): Promise<BingoListItem[]> {
    // Support both legacy (version string) and new (opts object) call signatures
    let v: GameVersion;
    let qs: URLSearchParams | undefined;
    if (typeof opts === 'string' || opts === undefined) {
      // Legacy call: getBingoList() or getBingoList('7')
      v = (opts as GameVersion | undefined) || getCurrentVersion();
    } else {
      // New call: getBingoList({ sort, filter }, '7')
      v = version || getCurrentVersion();
      const params = new URLSearchParams();
      if (opts.sort) params.set('sort', opts.sort);
      if (opts.filter) params.set('filter', opts.filter);
      if (params.toString()) qs = params;
    }
    const key = `bingo-list:${v}:${qs?.toString() ?? ''}`;
    const baseUrl = getVersionedBaseUrl(v);
    const url = qs ? `${baseUrl}/bingo?${qs.toString()}` : `${baseUrl}/bingo`;
    return dedupeRequest(key, async () => {
      const response = await fetch(url, { headers: this.getAuthHeaders() });
      if (!response.ok) throw new Error(`API Error: ${response.status} ${response.statusText}`);
      return response.json() as Promise<BingoListItem[]>;
    });
  }

  async getMyBingoList(
    opts?: { sort?: 'updated' | 'created' | 'bookmark' | 'name' } | GameVersion,
    version?: GameVersion,
  ): Promise<BingoListItem[]> {
    let v: GameVersion;
    let qs: URLSearchParams | undefined;
    if (typeof opts === 'string' || opts === undefined) {
      v = (opts as GameVersion | undefined) || getCurrentVersion();
    } else {
      v = version || getCurrentVersion();
      const params = new URLSearchParams();
      if (opts.sort) params.set('sort', opts.sort);
      if (params.toString()) qs = params;
    }
    const baseUrl = getVersionedBaseUrl(v);
    const url = qs ? `${baseUrl}/bingo/my?${qs.toString()}` : `${baseUrl}/bingo/my`;
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async createBingo(data: { bingoName: string; description?: string | null; songTable: Array<{ songIdx: number; difficulty: string; targetScore: number; targetExPercent?: number | null; targetMaxMinus?: number | null }>; visibility?: string; bingoType?: 'SCORE' | 'EX_SCORE' }, version?: GameVersion): Promise<{ bingoId: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to create bingo: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async updateBingo(bingoId: string, data: { bingoName: string; description?: string | null; songTable: Array<{ songIdx: number; difficulty: string; targetScore: number; targetExPercent?: number | null; targetMaxMinus?: number | null }>; visibility?: string; bingoType?: 'SCORE' | 'EX_SCORE' }, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo/${bingoId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to update bingo: ${response.status} ${response.statusText}`);
    }
  }

  async bookmarkBingo(bingoId: string, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo/${bingoId}/bookmark`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to bookmark bingo: ${response.status} ${response.statusText}`);
    }
  }

  async unbookmarkBingo(bingoId: string, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo/${bingoId}/bookmark`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to unbookmark bingo: ${response.status} ${response.statusText}`);
    }
  }

  async deleteBingo(bingoId: string, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo/${bingoId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to delete bingo: ${response.status} ${response.statusText}`);
    }
  }

  async generateRandomBingo(data: { levelMin: number; levelMax: number; scoreType: 'FIXED' | 'MY_LEVEL'; fixedScore?: number }, version?: GameVersion): Promise<Array<{ songIdx: number; songName: string; difficulty: string; level: number; imageUrl: string; targetScore: number }>> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/bingo/random`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to generate random bingo: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // === Comment endpoints (v7 only) ===
  async getComments(): Promise<SongComment[]> {
    const key = `comments:7`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<SongComment[]>('/comment', undefined, '7')
    );
  }

  async getComment(songId: number, difficulty: string): Promise<SongComment | null> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/comment/${songId}/${difficulty}`, {
      headers: this.getAuthHeaders(),
    });

    // 204 = 코멘트 없음 (현행). 404 도 함께 처리해 backend rollout 중간 상태에서도 깨지지 않게.
    if (response.status === 204 || response.status === 404) {
      return null;
    }

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = '/7/login';
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async saveComment(songId: number, difficulty: string, comment: string): Promise<SongComment> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/comment/${songId}/${difficulty}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ comment }),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = '/7/login';
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to save comment: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async deleteComment(songId: number, difficulty: string): Promise<void> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/comment/${songId}/${difficulty}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = '/7/login';
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to delete comment: ${response.status} ${response.statusText}`);
    }
  }

  async getSongIdsWithComments(): Promise<number[]> {
    const key = `comment-songs:7`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<number[]>('/comment/songs', undefined, '7')
    );
  }

  // 이미지 프록시 URL 생성
  getProxyImageUrl(originalUrl: string): string {
    if (!originalUrl) return originalUrl;
    
    // Check if proxy should be used based on environment
    const useProxy = import.meta.env.VITE_USE_IMAGE_PROXY === 'true';
    
    if (!useProxy) {
      // Production: use images directly
      // If the URL is relative or starts with /images, assume it's served from the same server
      if (originalUrl.startsWith('/') || originalUrl.startsWith('images/')) {
        // Return as-is for internal server images
        return originalUrl;
      }
      // For external URLs in production (if any), return as-is
      return originalUrl;
    }
    
    // Development: use proxy to bypass CORS
    // For relative paths in development, need to proxy through backend
    if (originalUrl.startsWith('/') || originalUrl.startsWith('images/')) {
      // Construct the full URL for internal images
      const baseUrl = API_BASE_URL.replace('/api/v1', '');
      return `${API_BASE_URL}/proxy?url=${encodeURIComponent(baseUrl + originalUrl)}`;
    }
    
    // For external URLs, proxy them
    return `${API_BASE_URL}/proxy?url=${encodeURIComponent(originalUrl)}`;
  }

  // === User endpoints (versioned) ===
  async register(data: RegisterRequest, version?: GameVersion): Promise<RegisterResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `회원가입 실패: ${response.status}`);
    }

    return response.json();
  }

  async login(data: LoginRequest, version?: GameVersion): Promise<LoginResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `로그인 실패: ${response.status}`);
    }

    return response.json();
  }

  async deleteAccount(password: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/user/me`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ password }),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      let message = `회원 탈퇴 실패: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData?.message) message = errorData.message;
      } catch {
        // ignore parse errors
      }
      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
  }

  async updateUser(data: UpdateUserRequest, version?: GameVersion): Promise<UpdateUserResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `사용자 정보 수정 실패: ${response.status}`);
    }

    return response.json();
  }

  async updatePartialVisibility(
    mode: 'ALL_MUTUAL' | 'SELECTED',
    allowedRivalIds: string[],
    version?: GameVersion,
  ): Promise<{ success: string; message: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/partial-visibility`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ mode, allowedRivalIds }),
    });
    if (!response.ok) {
      throw new Error(`Failed to update partial visibility: ${response.status}`);
    }
    return response.json();
  }

  async getProfile(version?: GameVersion): Promise<UserProfileResponse> {
    const v = version || getCurrentVersion();
    const key = `user-profile:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<UserProfileResponse>('/user/profile', undefined, version)
    );
  }

  async updateLanguage(language: string, version?: GameVersion): Promise<UpdateUserResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/language`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ language }),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async updateScoreThresholds(thresholds: ScoreThreshold[], version?: GameVersion): Promise<UpdateUserResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/score-thresholds`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(thresholds),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /** 버전별 플레이 횟수 조회 (v7 전용) */
  async getPlayCountHistory(): Promise<PlayCountHistory> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/user/play-count-history`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /** 버전별 플레이 횟수 수동 입력 / 노출 토글 (v7 전용) */
  async updatePlayCountHistory(
    entries: PlayCountEntryUpdate[],
    visible: boolean,
  ): Promise<PlayCountHistory> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/user/play-count-history`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ entries, visible }),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async updateExScoreThresholds(thresholds: ExScoreThreshold[], version?: GameVersion): Promise<UpdateUserResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/exscore-thresholds`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(thresholds),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // === Konasute Pack endpoints (versioned) ===
  async getKonasutePacks(version?: GameVersion): Promise<KonasutePackInfo[]> {
    return this.fetchVersionedApi<KonasutePackInfo[]>('/user/konasute-packs', undefined, version);
  }

  async getKonasuteHistory(
    params: KonasuteHistoryParams,
    version?: GameVersion
  ): Promise<KonasutePlayLogPage> {
    const query: Record<string, string | number> = {};
    if (params.before) query.before = params.before;
    if (params.size != null) query.size = params.size;
    if (params.minScore != null && params.minScore > 0) query.minScore = params.minScore;
    return this.fetchVersionedApi<KonasutePlayLogPage>('/konasute/history', query, version);
  }

  async getKonasuteLevelTrend(
    window: number,
    version?: GameVersion
  ): Promise<LevelTrendResponse> {
    return this.fetchVersionedApi<LevelTrendResponse>('/konasute/level-trend', { window }, version);
  }

  async getKonasuteRecentScores(
    n: number,
    version?: GameVersion
  ): Promise<RecentScoresResponse> {
    return this.fetchVersionedApi<RecentScoresResponse>('/konasute/recent-scores', { n }, version);
  }

  async updateKonasuteEnable(enable: boolean, version?: GameVersion): Promise<UpdateUserResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/konasute-enable`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ enable }),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || 'Failed to update konasute enable');
    }

    return response.json();
  }

  async updateOwnedKonasutePacks(packIds: string[], version?: GameVersion): Promise<UpdateUserResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/owned-konasute-packs`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(packIds),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // === Konasute Hook (v7 전용, 토큰 발급 / 설정 파일 / 어드민 부여) ===
  async getKonasuteHookStatus(): Promise<KonasuteHookStatus> {
    return this.fetchApi<KonasuteHookStatus>('/7/user/konasute-hook/status');
  }

  /** 권한 있으면 토큰 발급(없을 때만) 후 설정 파일 내용 반환. 미부여 시 403 → Error */
  async getKonasuteHookConfig(): Promise<KonasuteHookConfig> {
    return this.fetchApi<KonasuteHookConfig>('/7/user/konasute-hook/config');
  }

  async regenerateKonasuteHookToken(): Promise<KonasuteHookConfig> {
    const response = await fetch(`${API_BASE_URL}/7/user/konasute-hook/token/regenerate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to regenerate konasute hook token: ${response.status}`);
    }

    return response.json();
  }

  async getKonasuteHookUsers(): Promise<KonasuteHookGrantedUser[]> {
    return this.fetchApi<KonasuteHookGrantedUser[]>('/manage/konasute-hook/users');
  }

  async grantKonasuteHook(userIds: string[]): Promise<KonasuteHookGrantResult> {
    const response = await fetch(`${API_BASE_URL}/manage/konasute-hook/grant`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });
    if (!response.ok) throw new Error(`Failed to grant konasute hook: ${response.status}`);
    return response.json();
  }

  async revokeKonasuteHook(userIds: string[]): Promise<KonasuteHookGrantResult> {
    const response = await fetch(`${API_BASE_URL}/manage/konasute-hook/revoke`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });
    if (!response.ok) throw new Error(`Failed to revoke konasute hook: ${response.status}`);
    return response.json();
  }

  // === Discord Link endpoints (common - non-versioned) ===
  async verifyDiscordLink(code: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_BASE_URL}/user/discord-link/verify`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ code }),
    });

    this.checkAchievementHeader(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Discord 연동에 실패했습니다.');
    }

    return data;
  }

  // 레거시 로그인 (common - auth endpoint)
  async loginLegacy(userId: string): Promise<LegacyLoginResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      throw new Error(`Login failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // === Rival endpoints (versioned) ===
  async getRivalList(params: RivalListParams = {}, version?: GameVersion): Promise<RivalListResponse> {
    const v = version || getCurrentVersion();
    const normalizedParams = {
      levelRangeLow: params.levelRangeLow ?? 1,
      levelRangeHigh: params.levelRangeHigh ?? 20,
      isScore: params.isScore !== undefined ? params.isScore : true
    };
    const key = `rival-list:${v}:${JSON.stringify(normalizedParams)}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<RivalListResponse>('/rival', normalizedParams, version)
    );
  }

  async addRival(rivalId: string, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/rival`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ rivalId }),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to add rival: ${response.status} ${response.statusText}`);
    }
  }

  async removeRival(rivalId: string, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/rival`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ rivalId }),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to remove rival: ${response.status} ${response.statusText}`);
    }
  }

  async getRivalRecords(songId: number, diff: string, version?: GameVersion): Promise<RivalRecordsResponse> {
    return this.fetchVersionedApi<RivalRecordsResponse>('/rival/records', { songId, diff }, version);
  }

  async getRivalRecordsBatch(pairs: RivalRecordsBatchPair[], version?: GameVersion): Promise<RivalRecordsBatchResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/rival/records/batch`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ pairs }),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch rival records batch: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async getRivalComparison(params: {
    rivalId: string;
    levelLow: number;
    levelHigh: number;
    comparisonType: 'SCORE' | 'EX_SCORE';
    sortOption: 'ASC' | 'DESC';
  }, version?: GameVersion): Promise<RivalComparisonResponse> {
    return this.fetchVersionedApi<RivalComparisonResponse>('/rival/comparison', params, version);
  }

  async getDuelComparison(params: {
    targetId: string;
    levelLow: number;
    levelHigh: number;
    comparisonType: 'SCORE' | 'EX_SCORE';
    sortOption: 'ASC' | 'DESC';
  }, version?: GameVersion): Promise<RivalComparisonResponse> {
    return this.fetchVersionedApi<RivalComparisonResponse>('/rival/duel', {
      targetId: params.targetId,
      levelLow: params.levelLow,
      levelHigh: params.levelHigh,
      comparisonType: params.comparisonType,
      sortOption: params.sortOption,
    }, version);
  }

  async getRivalTrend(params: {
    rivalId: string;
    levelLow?: number;
    levelHigh?: number;
    comparisonType?: 'SCORE' | 'EX_SCORE';
  }, version?: GameVersion): Promise<RivalTrendResponse> {
    return this.fetchVersionedApi<RivalTrendResponse>('/rival/trend', params, version);
  }

  /**
   * 이름·볼포스만 담은 경량 라이벌 목록. 승/무/패 비교를 돌리지 않아 선택 UI 용으로 훨씬 빠르다.
   * 승패·에셋이 필요하면 getRivalList 를 쓴다.
   */
  async getSimpleRivalList(version?: GameVersion): Promise<RivalSimple[]> {
    const v = version || getCurrentVersion();
    return dedupeRequest(`rival-simple:${v}`, () =>
      this.fetchVersionedApi<RivalSimple[]>('/rival/simple', {}, version)
    );
  }

  async getFollowers(version?: GameVersion): Promise<RivalFollower[]> {
    return this.fetchVersionedApi<RivalFollower[]>('/rival/followers', {}, version);
  }

  // Mock search function - 실제로는 사용자 검색 API가 필요할 것 같습니다
  async searchUsers(query: string): Promise<{ userId: string; nickName: string; volforce: number }[]> {
    // Mock implementation - 실제 API가 생기면 교체 필요
    if (!query) return [];

    return [
      { userId: 'user1', nickName: `SEARCH_USER_1_${query}`, volforce: 17.890 },
      { userId: 'user2', nickName: `SEARCH_USER_2_${query}`, volforce: 18.456 }
    ];
  }

  // === Difficulty Table endpoints (versioned) ===
  async getDiffTableList(version?: GameVersion): Promise<DiffTableListItem[]> {
    const v = version || getCurrentVersion();
    const key = `difftable-list:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<DiffTableListItem[]>('/diffTable/list', undefined, version)
    );
  }

  async getDiffTableDetail(diffTableId: string, version?: GameVersion, tagId?: string | null): Promise<DiffTableDetailResponse> {
    const v = version || getCurrentVersion();
    const key = `difftable-detail:${v}:${diffTableId}:${tagId ?? ''}`;
    const params: Record<string, string> = { diffTableId };
    if (tagId && v === '7') params.tagId = tagId;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<DiffTableDetailResponse>('/diffTable/detail', params, version)
    );
  }

  /**
   * 공개 난이도표 상세 조회 (공유 링크로 다른 유저 기록을 난이도표로 조회)
   * @param userName 대상 유저의 userId(SV-XXXX) 또는 username
   */
  async getPublicDiffTableDetail(userName: string, diffTableId: string, version?: GameVersion, tagId?: string | null): Promise<DiffTableDetailResponse> {
    const v = version || getCurrentVersion();
    const key = `difftable-public-detail:${v}:${userName}:${diffTableId}:${tagId ?? ''}`;
    const params: Record<string, string> = { diffTableId };
    if (tagId && v === '7') params.tagId = tagId;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<DiffTableDetailResponse>(`/diffTable/public/${encodeURIComponent(userName)}/detail`, params, version)
    );
  }

  /**
   * 커스텀 난이도표 조회
   * @param levels 콤마로 구분된 레벨 목록 (예: "17.0,17.5,18.0")
   * @param groupBy 그룹핑 기준 ("level", "radarType", "date", "clearType", "none")
   */
  async getCustomDiffTable(levels: string, groupBy: string = 'level', version?: GameVersion, tagId?: string | null): Promise<DiffTableDetailResponse> {
    const v = version || getCurrentVersion();
    const key = `difftable-custom:${v}:${levels}:${groupBy}:${tagId ?? ''}`;
    const params: Record<string, string> = { levels, groupBy };
    if (tagId && v === '7') params.tagId = tagId;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<DiffTableDetailResponse>('/diffTable/custom', params, version)
    );
  }

  /**
   * 공개 커스텀 난이도표 조회 (다른 유저 기록으로 커스텀 표 생성)
   * @param userName 대상 유저의 userId(SV-XXXX) 또는 username
   */
  async getPublicCustomDiffTable(userName: string, levels: string, groupBy: string = 'level', version?: GameVersion, tagId?: string | null): Promise<DiffTableDetailResponse> {
    const v = version || getCurrentVersion();
    const key = `difftable-public-custom:${v}:${userName}:${levels}:${groupBy}:${tagId ?? ''}`;
    const params: Record<string, string> = { levels, groupBy };
    if (tagId && v === '7') params.tagId = tagId;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<DiffTableDetailResponse>(`/diffTable/public/${encodeURIComponent(userName)}/custom`, params, version)
    );
  }

  // === Playlist endpoints (versioned) ===
  async getPublicPlaylistList(version?: GameVersion): Promise<PlaylistListItem[]> {
    const v = version || getCurrentVersion();
    const key = `playlist-public:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<PlaylistListItem[]>('/playlist', undefined, version)
    );
  }

  async getMyPlaylistList(version?: GameVersion): Promise<PlaylistListItem[]> {
    const v = version || getCurrentVersion();
    const key = `playlist-my:${v}`;
    return dedupeRequest(key, () =>
      this.fetchVersionedApi<PlaylistListItem[]>('/playlist/my', undefined, version)
    );
  }

  async getPlaylistDetail(playlistId: string, version?: GameVersion): Promise<PlaylistDetailResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/playlist/${playlistId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('이 플레이리스트에 접근할 권한이 없습니다.');
      }
      if (response.status === 404) {
        throw new Error('플레이리스트를 찾을 수 없습니다.');
      }
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async createPlaylist(data: CreatePlaylistRequest, version?: GameVersion): Promise<CreatePlaylistResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/playlist`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `플레이리스트 생성 실패: ${response.status}`);
    }

    return response.json();
  }

  async updatePlaylist(playlistId: string, data: UpdatePlaylistRequest, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/playlist/${playlistId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('이 플레이리스트를 수정할 권한이 없습니다.');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `플레이리스트 수정 실패: ${response.status}`);
    }
  }

  async deletePlaylist(playlistId: string, version?: GameVersion): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/playlist/${playlistId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('이 플레이리스트를 삭제할 권한이 없습니다.');
    }

    if (!response.ok) {
      throw new Error(`플레이리스트 삭제 실패: ${response.status}`);
    }
  }

  // === Admin API endpoints (common - non-versioned) ===

  /**
   * 유의어 목록 조회
   * GET /api/v1/manage/synonyms
   * GET /api/v1/manage/synonyms?songId={id}
   */
  async getSynonyms(songId?: number): Promise<SynonymListResponse> {
    const params = songId !== undefined ? { songId } : undefined;
    return this.fetchApi<SynonymListResponse>('/manage/synonyms', params);
  }

  /**
   * 유의어 추가
   * POST /api/v1/manage/synonyms
   */
  async addSynonym(request: AddSynonymRequest): Promise<SynonymDto> {
    const response = await fetch(`${API_BASE_URL}/manage/synonyms`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`유의어 추가 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 유의어 일괄 추가
   * POST /api/v1/manage/synonyms/batch
   */
  async addSynonymsBatch(request: BatchAddSynonymRequest): Promise<BatchSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/synonyms/batch`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`유의어 일괄 추가 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 유의어 삭제
   * DELETE /api/v1/manage/synonyms/{id}
   */
  async deleteSynonym(id: string): Promise<DeleteSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/synonyms/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`유의어 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 특정 곡의 모든 유의어 삭제
   * DELETE /api/v1/manage/synonyms/song/{songId}
   */
  async deleteSynonymsBySongId(songId: number): Promise<DeleteSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/synonyms/song/${songId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`유의어 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  // ===== 태그 마스터 (ADVANCED 이상) =====

  /**
   * 태그 목록 조회
   * GET /api/v1/manage/tags
   */
  async listTags(): Promise<Tag[]> {
    return this.fetchApi<Tag[]>('/manage/tags');
  }

  /**
   * 태그 생성
   * POST /api/v1/manage/tags
   */
  async createTag(request: TagUpsertRequest): Promise<Tag> {
    const response = await fetch(`${API_BASE_URL}/manage/tags`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`태그 생성 실패: ${response.status}`);
    return response.json();
  }

  /**
   * 태그 수정
   * PUT /api/v1/manage/tags/{id}
   */
  async updateTag(id: string, request: TagUpsertRequest): Promise<Tag> {
    const response = await fetch(`${API_BASE_URL}/manage/tags/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`태그 수정 실패: ${response.status}`);
    return response.json();
  }

  /**
   * 태그 삭제
   * DELETE /api/v1/manage/tags/{id}
   */
  async deleteTag(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/manage/tags/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`태그 삭제 실패: ${response.status}`);
  }

  // ===== 채보 태그 (v7) =====

  /**
   * 에디터용 전체 태그 목록 조회
   * GET /api/v1/7/song/tags
   */
  async listAllTagsForEditor(): Promise<Tag[]> {
    return this.fetchApi<Tag[]>('/7/song/tags');
  }

  /**
   * GET /api/v1/7/user/tag-analysis?minScore=...&minExPercent=...
   * @param minScore     score 평균 산입 최소 점수. 기본 9,000,000.
   * @param minExPercent EX 평균 산입 최소 percentage. 기본 90.
   */
  async getTagAnalysis(
    minScore: number = 9_000_000,
    minExPercent: number = 90,
  ): Promise<TagAnalysisResponse> {
    return this.fetchApi<TagAnalysisResponse>(
      `/7/user/tag-analysis?minScore=${minScore}&minExPercent=${minExPercent}`,
    );
  }

  /**
   * GET /api/v1/7/user/tag-analysis/trend?axis=TAG|LEVEL&mode=SCORE|EX_SCORE
   */
  async getScoreTrend(
    axis: ScoreTrendAxis,
    mode: 'SCORE' | 'EX_SCORE' = 'SCORE',
  ): Promise<ScoreTrendResponse> {
    return this.fetchApi<ScoreTrendResponse>(
      `/7/user/tag-analysis/trend?axis=${axis}&mode=${mode}`,
    );
  }

  /**
   * GET /api/v1/7/user/tag-ranking?levelMin=17&levelMax=18
   */
  async getTagRanking(levelMin: number, levelMax: number): Promise<TagRankingResponse> {
    return this.fetchApi<TagRankingResponse>(`/7/user/tag-ranking?levelMin=${levelMin}&levelMax=${levelMax}`);
  }

  /**
   * GET /api/v1/export/tagRanking?levelMin=17&levelMax=18
   */
  async exportTagRanking(levelMin: number, levelMax: number, imageFormat: 'webp' | 'png' = 'webp'): Promise<Blob> {
    const token = localStorage.getItem('authToken');
    const response = await fetch(
      `${API_BASE_URL}/export/tagRanking?levelMin=${levelMin}&levelMax=${levelMax}&imageFormat=${imageFormat}`,
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      }
    );
    if (!response.ok) {
      throw new Error('Export failed');
    }
    return response.blob();
  }

  /**
   * GET /api/v1/7/song/similar
   */
  async getSimilarCharts(
    songIdx: number,
    difficulty: string,
    limit = 10,
  ): Promise<SimilarChartsResponse> {
    const params = new URLSearchParams({
      songIdx: String(songIdx),
      difficulty,
      limit: String(limit),
    });
    return this.fetchApi<SimilarChartsResponse>(`/7/song/similar?${params}`);
  }

  /**
   * 태그가 부여된 v7 채보 목록 조회
   * GET /api/v1/7/song/tags/charts
   */
  async listTaggedCharts(): Promise<{ songIdx: number; difficulty: string; level: number }[]> {
    return this.fetchApi<{ songIdx: number; difficulty: string; level: number }[]>('/7/song/tags/charts');
  }

  /**
   * 채보 태그 조회
   * GET /api/v1/7/song/{songIdx}/{difficulty}/tags
   */
  async getChartTags(songIdx: number, difficulty: string): Promise<TagWithIntensity[]> {
    return this.fetchApi<TagWithIntensity[]>(`/7/song/${songIdx}/${difficulty}/tags`);
  }

  /**
   * 채보 태그 저장 (일괄 교체)
   * PUT /api/v1/7/song/{songIdx}/{difficulty}/tags
   */
  async setChartTags(
    songIdx: number,
    difficulty: string,
    assignments: TagAssignmentDto[],
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/7/song/${songIdx}/${difficulty}/tags`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(assignments),
      },
    );
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`태그 저장 실패: ${response.status}`);
  }

  /**
   * 캐시 강제 갱신
   * POST /api/v1/manage/song-cache/refresh
   */
  async refreshSongCache(): Promise<CacheRefreshResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/song-cache/refresh`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`캐시 갱신 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 캐시 통계 조회
   * GET /api/v1/manage/song-cache/stats
   */
  async getCacheStats(): Promise<CacheStatsResponse> {
    return this.fetchApi<CacheStatsResponse>('/manage/song-cache/stats');
  }

  // === 아티스트 유의어 API endpoints ===

  /**
   * 아티스트 유의어 목록 조회
   * GET /api/v1/manage/artist-synonyms
   * GET /api/v1/manage/artist-synonyms?artistName={name}
   */
  async getArtistSynonyms(artistName?: string): Promise<ArtistSynonymListResponse> {
    const params = artistName !== undefined ? { artistName } : undefined;
    return this.fetchApi<ArtistSynonymListResponse>('/manage/artist-synonyms', params);
  }

  /**
   * 아티스트 유의어 추가
   * POST /api/v1/manage/artist-synonyms
   */
  async addArtistSynonym(request: AddArtistSynonymRequest): Promise<ArtistSynonymDto> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-synonyms`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 유의어 추가 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 아티스트 유의어 삭제
   * DELETE /api/v1/manage/artist-synonyms/{id}
   */
  async deleteArtistSynonym(id: string): Promise<DeleteSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-synonyms/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 유의어 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 특정 아티스트의 모든 유의어 삭제
   * DELETE /api/v1/manage/artist-synonyms/artist/{artistName}
   */
  async deleteArtistSynonymsByArtistName(artistName: string): Promise<DeleteSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-synonyms/artist/${encodeURIComponent(artistName)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 유의어 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  // === 아티스트 링크 API endpoints ===
  // 복합 아티스트(예: "A vs B feat C")를 개별 아티스트(A, B, C)에 연결

  /**
   * 아티스트 링크 목록 조회
   * GET /api/v1/manage/artist-links
   * GET /api/v1/manage/artist-links?sourceArtist={name}
   * GET /api/v1/manage/artist-links?linkedArtist={name}
   */
  async getArtistLinks(params?: { sourceArtist?: string; linkedArtist?: string }): Promise<ArtistLinkListResponse> {
    return this.fetchApi<ArtistLinkListResponse>('/manage/artist-links', params);
  }

  /**
   * 아티스트 링크 추가
   * POST /api/v1/manage/artist-links
   */
  async addArtistLink(request: AddArtistLinkRequest): Promise<ArtistLinkDto> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-links`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 링크 추가 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 아티스트 링크 일괄 추가
   * POST /api/v1/manage/artist-links/batch
   */
  async addArtistLinksBatch(request: BatchAddArtistLinkRequest): Promise<BatchArtistLinkResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-links/batch`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 링크 일괄 추가 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 아티스트 링크 삭제
   * DELETE /api/v1/manage/artist-links/{id}
   */
  async deleteArtistLink(id: string): Promise<DeleteSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-links/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 링크 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 특정 복합 아티스트의 모든 링크 삭제
   * DELETE /api/v1/manage/artist-links/source/{sourceArtist}
   */
  async deleteArtistLinksBySourceArtist(sourceArtist: string): Promise<DeleteSynonymResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/artist-links/source/${encodeURIComponent(sourceArtist)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 링크 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 특정 링크 삭제 (sourceArtist + linkedArtist로 삭제)
   * DELETE /api/v1/manage/artist-links/link?sourceArtist={source}&linkedArtist={linked}
   */
  async deleteArtistLinkByPair(sourceArtist: string, linkedArtist: string): Promise<DeleteSynonymResponse> {
    const url = new URL(`${API_BASE_URL}/manage/artist-links/link`, window.location.origin);
    url.searchParams.append('sourceArtist', sourceArtist);
    url.searchParams.append('linkedArtist', linkedArtist);

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`아티스트 링크 삭제 실패: ${response.status}`);
    }

    return response.json();
  }

  // === 곡 난이도 정보 수정 API ===

  /**
   * 곡 난이도 정보 수정 (chipCount, holdCount, tsumamiCount, radar)
   * POST /api/v1/admin/updateSongDifficulty
   */
  async updateSongDifficulty(request: UpdateSongDifficultyRequest): Promise<UpdateSongDifficultyResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/updateSongDifficulty`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`곡 난이도 정보 수정 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 전체 곡 목록 조회 (관리자용)
   * GET /api/v1/manage/allSongs
   */
  async getAllSongsForAdmin(): Promise<{ songIdx: number; songName: string; artist: string; imageUrl: string | null }[]> {
    const response = await fetch(`${API_BASE_URL}/manage/allSongs`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (response.status === 403) {
      throw new Error('관리자 권한이 필요합니다.');
    }

    if (!response.ok) {
      throw new Error(`전체 곡 목록 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 곡 추가 (관리자용)
   * POST /api/v1/manage/addSong
   */
  async addSong(request: import('../types/admin').AddSongRequest): Promise<import('../types/admin').AddSongResponse> {
    const response = await fetch(`${API_BASE_URL}/manage/addSong`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${getCurrentVersion()}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`곡 추가 실패: ${response.status}`);
    }

    return response.json();
  }

  // === Group Comparison endpoints (versioned) ===

  /**
   * 그룹 비교 수행
   * POST /api/v1/{6|7}/group/compare
   */
  async compareGroups(
    request: {
      groupAUserIds: string[];
      groupBUserIds: string[];
      levelRangeLow: number;
      levelRangeHigh: number;
      onlyAllPlayed?: boolean;
      useAverage?: boolean;
      sortOrder?: 'A_WIN_DESC' | 'A_WIN_ASC';
    },
    version?: GameVersion
  ): Promise<{
    groupAUsers: { userId: string; displayName: string; volforce: number | null }[];
    groupBUsers: { userId: string; displayName: string; volforce: number | null }[];
    records: {
      albumArt: string;
      songId: number;
      songName: string;
      songArtist: string;
      songLevel: number;
      songDiff: string;
      groupAScores: { userId: string; displayName: string; score: number }[];
      groupBScores: { userId: string; displayName: string; score: number }[];
      groupAValue: number;
      groupBValue: number;
      valueDiff: number;
    }[];
    summary: {
      groupAWins: number;
      groupBWins: number;
      draws: number;
    };
  }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/group/compare`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 그룹 비교용 사용자 검색
   * GET /api/v1/{6|7}/group/search-user?identifier={identifier}
   */
  async searchGroupUser(
    identifier: string,
    version?: GameVersion
  ): Promise<{ userId: string; displayName: string; volforce: number | null } | null> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/group/search-user?identifier=${encodeURIComponent(identifier)}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      // 403: 비공개 설정으로 조회 불가
      if (response.status === 403) {
        try {
          const errorData = await response.json();
          if (errorData.code === 'RECORD_PRIVATE') {
            throw new Error('비공개 설정된 사용자입니다.');
          } else if (errorData.code === 'RECORD_PARTIAL_DENIED') {
            throw new Error('상호 라이벌인 경우에만 조회할 수 있습니다. (상대방도 나를 라이벌로 등록해야 함)');
          }
          throw new Error('비공개 설정된 사용자입니다.');
        } catch (e) {
          if (e instanceof Error && e.message.includes('비공개') || e instanceof Error && e.message.includes('상호')) {
            throw e;
          }
          throw new Error('비공개 설정된 사용자입니다.');
        }
      }
      // 401: 인증 필요
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userId');
        window.location.href = `/${v}/login`;
        throw new Error('Authentication required');
      }
      return null;
    }

    const text = await response.text();
    if (!text) return null;

    return JSON.parse(text);
  }

  /**
   * 난이도표 점수 구간 설정 조회
   * GET /api/v1/{6|7}/user/difftable-score-ranges/{levelKey}
   */
  async getDiffTableScoreRanges(
    levelKey: string,
    version?: GameVersion
  ): Promise<{
    ranges: Array<{ score: number; color: string | null; action?: 'color' | 'hide' }>;
    hideNoPlay?: boolean;
    remainderAction?: 'show' | 'hide';
    remainderColor?: string | null;
  } | null> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/difftable-score-ranges/${levelKey}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    // 백엔드에서 ranges만 반환하는 경우 하위 호환성 유지
    if (Array.isArray(data)) {
      return {
        ranges: data.map((r: { score: number; color: string | null; action?: string }) => ({
          score: r.score,
          color: r.color,
          action: r.action as 'color' | 'hide' | undefined
        })),
        hideNoPlay: false,
        remainderAction: 'show',
        remainderColor: null
      };
    }
    return data;
  }

  /**
   * 난이도표 점수 구간 설정 저장
   * PUT /api/v1/{6|7}/user/difftable-score-ranges/{levelKey}
   */
  async saveDiffTableScoreRanges(
    levelKey: string,
    settings: {
      ranges: Array<{ score: number; color: string | null; action?: 'color' | 'hide' }>;
      hideNoPlay?: boolean;
      remainderAction?: 'show' | 'hide';
      remainderColor?: string | null;
    },
    version?: GameVersion
  ): Promise<{ success: string; message: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    // Backend expects levelKey in the body as well
    const requestBody = {
      levelKey,
      ...settings,
    };
    const response = await fetch(`${baseUrl}/user/difftable-score-ranges/${levelKey}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 난이도표 점수 구간 설정 삭제
   * DELETE /api/v1/{6|7}/user/difftable-score-ranges/{levelKey}
   */
  async deleteDiffTableScoreRanges(
    levelKey: string,
    version?: GameVersion
  ): Promise<{ success: string; message: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/difftable-score-ranges/${levelKey}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 난이도표 Ex-Score 구간 설정 조회
   * GET /api/v1/{6|7}/user/difftable-exscore-ranges/{levelKey}
   */
  async getDiffTableExScoreRanges(
    levelKey: string,
    version?: GameVersion
  ): Promise<{
    ranges: Array<{
      thresholdType?: 'percent' | 'maxMinus';
      percent: number;
      maxMinus?: number;
      color: string | null;
      action?: 'color' | 'hide';
    }>;
    hideNoPlay?: boolean;
    remainderAction?: 'show' | 'hide';
    remainderColor?: string | null;
    displayFormat?: 'percent' | 'maxMinus';
  } | null> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/difftable-exscore-ranges/${levelKey}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  }

  /**
   * 난이도표 Ex-Score 구간 설정 저장
   * PUT /api/v1/{6|7}/user/difftable-exscore-ranges/{levelKey}
   */
  async saveDiffTableExScoreRanges(
    levelKey: string,
    settings: {
      ranges: Array<{
        thresholdType: 'percent' | 'maxMinus';
        percent: number;
        maxMinus: number;
        color: string | null;
        action?: 'color' | 'hide';
      }>;
      hideNoPlay?: boolean;
      remainderAction?: 'show' | 'hide';
      remainderColor?: string | null;
      displayFormat?: 'percent' | 'maxMinus';
    },
    version?: GameVersion
  ): Promise<{ success: string; message: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const requestBody = {
      levelKey,
      ...settings,
    };
    const response = await fetch(`${baseUrl}/user/difftable-exscore-ranges/${levelKey}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(requestBody),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 난이도표 Ex-Score 구간 설정 삭제
   * DELETE /api/v1/{6|7}/user/difftable-exscore-ranges/{levelKey}
   */
  async deleteDiffTableExScoreRanges(
    levelKey: string,
    version?: GameVersion
  ): Promise<{ success: string; message: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/difftable-exscore-ranges/${levelKey}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Ex-Score 표시 형식 조회
   * GET /api/v1/{6|7}/user/exscore-display-format
   */
  async getExScoreDisplayFormat(
    version?: GameVersion
  ): Promise<'PERCENT' | 'MAX_MINUS'> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/exscore-display-format`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      return 'PERCENT'; // 기본값
    }

    const data = await response.json();
    return data.format || 'PERCENT';
  }

  /**
   * Ex-Score 표시 형식 저장
   * PUT /api/v1/{6|7}/user/exscore-display-format
   */
  async updateExScoreDisplayFormat(
    format: 'PERCENT' | 'MAX_MINUS',
    version?: GameVersion
  ): Promise<{ success: string; message: string }> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/exscore-display-format`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ format }),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * PUT /api/v1/{6|7}/user/detail-main-display
   */
  async updateDetailMainDisplay(
    format: 'PERCENT' | 'MAX_MINUS',
    version?: GameVersion
  ): Promise<void> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/user/detail-main-display`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ format }),
    });

    this.checkAchievementHeader(response);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  }

  // === Song Recommendation endpoints (versioned) ===

  /**
   * 곡 추천 요청
   * POST /api/v1/{6|7}/recommend
   */
  async recommendSongs(
    request: SongRecommendRequest,
    version?: GameVersion
  ): Promise<SongRecommendResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/recommend`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    this.checkAchievementHeader(response);

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      window.location.href = `/${v}/login`;
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 유사 실력대 중간값 사용 가능 여부 확인
   * GET /api/v1/{6|7}/recommend/can-use-median
   */
  async canUseMedianScore(
    version?: GameVersion
  ): Promise<CanUseMedianResponse> {
    const v = version || getCurrentVersion();
    const baseUrl = getVersionedBaseUrl(v);
    const response = await fetch(`${baseUrl}/recommend/can-use-median`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      return { canUseMedian: false };
    }

    return response.json();
  }

  // Analysis
  async getAnalysis(version?: GameVersion): Promise<AnalysisResponse | null> {
    try {
      return await this.fetchVersionedApi<AnalysisResponse>('/analysis', undefined, version);
    } catch {
      return null;
    }
  }

  async createAnalysis(levelMin: number, levelMax: number, version?: GameVersion): Promise<AnalysisResponse> {
    const v = version || (localStorage.getItem('gameVersion') as GameVersion) || '7';
    const baseUrl = `${API_BASE_URL}/${v}`;
    const response = await fetch(`${baseUrl}/analysis`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ levelMin, levelMax }),
    });
    this.checkAchievementHeader(response);
    if (response.status === 401) {
      window.location.href = `/${v}/login`;
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.errorMessage || `API Error: ${response.status}`);
    }
    return response.json();
  }

  async getAnalysisStatus(version?: GameVersion): Promise<AnalysisStatus> {
    return this.fetchVersionedApi<AnalysisStatus>('/analysis/status', undefined, version);
  }

  // === Notifications (v7) ===
  async listNotifications(beforeId?: string, limit = 30): Promise<NotificationDto[]> {
    const params: Record<string, unknown> = { limit };
    if (beforeId) params.beforeId = beforeId;
    return this.fetchApi<NotificationDto[]>('/7/notification', params);
  }

  async getUnreadNotificationCount(): Promise<number> {
    const res = await this.fetchApi<{ count: number }>('/7/notification/unread-count');
    return res.count;
  }

  async markNotificationsSeen(): Promise<{ lastSeenAt: string }> {
    const response = await fetch(`${API_BASE_URL}/7/notification/mark-seen`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async getVapidPublicKey(): Promise<string> {
    const res = await this.fetchApi<{ publicKey: string }>('/7/notification/push/vapid-key');
    return res.publicKey;
  }

  async subscribePushNotification(payload: PushSubscribePayload): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/7/notification/push/subscribe`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
  }

  async unsubscribePushNotification(endpoint: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/7/notification/push/unsubscribe`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ endpoint }),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
  }

  async listPushDevices(): Promise<PushDeviceDto[]> {
    return this.fetchApi<PushDeviceDto[]>('/7/notification/push/devices');
  }

  async testPushSelf(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/manage/notification/test-self`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
  }

  async broadcastNotification(req: {
    title: string;
    body: string;
    deepLink?: string;
    audience: { type: 'ALL' } | { type: 'USER_IDS'; ids: string[] };
  }): Promise<{ sentCount: number }> {
    const response = await fetch(`${API_BASE_URL}/manage/notification/broadcast`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(req),
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  /**
   * 온보딩 완료 처리
   * POST /api/v1/{6|7}/user/complete-init
   */
  async completeInit(req: {
    exScoreDisplayFormat: 'PERCENT' | 'MAX_MINUS';
    detailMainDisplay: 'PERCENT' | 'MAX_MINUS';
    scoreThresholds: import('../types/auth').ScoreThreshold[] | null;
    exScoreThresholds: import('../types/auth').ExScoreThresholdApi[] | null;
    konasuteEnable: boolean;
    recordVisibility?: import('../types/auth').RecordVisibility;
  }, gameVersion: GameVersion = '7'): Promise<void> {
    const baseUrl = getVersionedBaseUrl(gameVersion);
    const response = await fetch(`${baseUrl}/user/complete-init`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(req),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
  }

  /**
   * 차트 percentile 조회 (v7 전용)
   * GET /api/v1/7/song/percentile?songId=&diff=&mode=SCORE|EX_SCORE
   */
  async getChartPercentile(
    songId: number,
    diff: string,
    mode: 'SCORE' | 'EX_SCORE',
    version: GameVersion = '7'
  ): Promise<ChartPercentileResponse> {
    return this.fetchVersionedApi<ChartPercentileResponse>(
      '/song/percentile',
      { songId, diff, mode },
      version
    );
  }

  /**
   * 무기곡 찾기 결과 조회 (v7 전용)
   * GET /api/v1/7/util/weapon-songs?mode=SCORE|EX_SCORE&levelLow=&levelHigh=
   */
  async getWeaponSongs(
    mode: 'SCORE' | 'EX_SCORE',
    levelLow: number,
    levelHigh: number,
    version: GameVersion = '7'
  ): Promise<WeaponSongsResponse> {
    return this.fetchVersionedApi<WeaponSongsResponse>(
      '/util/weapon-songs',
      { mode, levelLow, levelHigh },
      version
    );
  }

  // === Profile Customization endpoints (non-versioned) ===
  async getProfileAssets(kind: AssetKind): Promise<ProfileAsset[]> {
    const response = await fetch(`${API_BASE_URL}/profile-assets?kind=${kind}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to fetch profile assets: ${response.status}`);
    return response.json();
  }

  async updateProfileCustomization(req: ProfileCustomizationRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/user/profile-customization`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(req),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`Failed to update customization: ${response.status}`);
  }

  // 사이트 UI 테마 장착. themeId null = 기본 테마로 리셋.
  async updateProfileTheme(themeId: string | null): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/user/profile-customization/theme`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ themeId }),
    });
    if (!response.ok) throw new Error(`Failed to update theme: ${response.status}`);
  }

  // ===== VGC 2026 승부예측 이벤트 =====
  async getVgcState(): Promise<VgcState> {
    return this.fetchVersionedApi<VgcState>('/vgc/state', undefined, '7');
  }

  async getVgcMatches(): Promise<VgcMatch[]> {
    return this.fetchVersionedApi<VgcMatch[]>('/vgc/matches', undefined, '7');
  }

  async predictVgcMatch(matchId: string, pick: VgcSide, boostCount: number): Promise<VgcMatch> {
    const res = await fetch(`${getVersionedBaseUrl('7')}/vgc/matches/${matchId}/predict`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ pick, boostCount }),
    });
    this.checkAchievementHeader(res);
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'PREDICT_FAILED');
    return res.json();
  }

  async claimVgcPlus(matchId: string): Promise<VgcMatch> {
    const res = await fetch(`${getVersionedBaseUrl('7')}/vgc/matches/${matchId}/plus`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    this.checkAchievementHeader(res);
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'PLUS_FAILED');
    return res.json();
  }

  async buyVgcBoosters(count: number): Promise<VgcWallet> {
    const res = await fetch(`${getVersionedBaseUrl('7')}/vgc/boosters/purchase`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ count }),
    });
    this.checkAchievementHeader(res);
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'BUY_FAILED');
    return res.json();
  }

  /** 내가 등록한 라이벌에게 포인트를 보낸다. 되돌릴 수 없다. */
  async giftVgcPoints(toUserId: string, amount: number): Promise<VgcWallet> {
    const res = await fetch(`${getVersionedBaseUrl('7')}/vgc/gift`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ toUserId, amount }),
    });
    this.checkAchievementHeader(res);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.code || 'GIFT_FAILED');
    }
    return res.json();
  }

  async getVgcShop(): Promise<VgcShopItem[]> {
    return this.fetchVersionedApi<VgcShopItem[]>('/vgc/shop', undefined, '7');
  }

  async purchaseVgcAsset(assetId: string): Promise<VgcShopItem> {
    const res = await fetch(`${getVersionedBaseUrl('7')}/vgc/shop/${assetId}/purchase`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    this.checkAchievementHeader(res);
    if (!res.ok) {
      // 히든/선착순 도입으로 실패 사유가 갈린다("sold out" / "not eligible" / "insufficient points").
      // code 는 셋 다 INVALID_STATUS 라 구분이 안 되므로 message 를 우선한다.
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || body.code || 'PURCHASE_FAILED');
    }
    const item = await res.json();
    // 해금 즉시 반영: 프로필 에셋 캐시 무효화(순환참조 회피 위해 동적 import)
    const mod = await import('../preview/hooks/useProfileAssets');
    mod.invalidateProfileAssetsCache();
    return item;
  }

  // Admin (under /api/v1/manage to match existing admin auth interceptor)
  async listAllProfileAssets(kind?: AssetKind): Promise<ProfileAsset[]> {
    const url = kind
      ? `${API_BASE_URL}/manage/profile-assets?kind=${kind}`
      : `${API_BASE_URL}/manage/profile-assets`;
    const response = await fetch(url, { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch admin profile assets: ${response.status}`);
    return response.json();
  }

  async uploadProfileAsset(kind: AssetKind, name: string, file: File): Promise<ProfileAsset> {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    form.append('name', name);
    const headers = this.getAuthHeaders();
    // FormData 는 boundary 포함 Content-Type 을 fetch 가 자동 설정해야 하므로 제거
    delete (headers as Record<string, string>)['Content-Type'];
    const response = await fetch(`${API_BASE_URL}/manage/profile-assets/upload`, {
      method: 'POST',
      headers,
      body: form,
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`Failed to upload profile asset: ${response.status}`);
    return response.json();
  }

  async updateProfileAsset(id: string, req: ProfileAssetUpdateRequest): Promise<ProfileAsset> {
    const response = await fetch(`${API_BASE_URL}/manage/profile-assets/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(req),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`Failed to update profile asset: ${response.status}`);
    return response.json();
  }

  async deleteProfileAsset(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/manage/profile-assets/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`Failed to delete profile asset: ${response.status}`);
  }

  async grantProfileAsset(id: string, userIds: string[]): Promise<AssetGrantResult> {
    const response = await fetch(`${API_BASE_URL}/manage/profile-assets/${encodeURIComponent(id)}/grant`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });
    if (!response.ok) throw new Error(`Failed to grant profile asset: ${response.status}`);
    return response.json();
  }

  // ===== VGC 관리자 =====
  async listVgcMatchesAdmin(): Promise<VgcMatch[]> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches`, { headers: this.getAuthHeaders() });
    if (!res.ok) throw new Error('LIST_FAILED');
    return res.json();
  }

  async createVgcMatch(body: {
    wave: number; title: string;
    optionA: { name: string; imageUrl?: string | null; members?: string[] };
    optionB: { name: string; imageUrl?: string | null; members?: string[] };
    closesAt?: string | null; sortOrder?: number;
  }): Promise<VgcMatch> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches`, {
      method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('CREATE_FAILED');
    return res.json();
  }

  async updateVgcMatch(id: string, body: {
    wave?: number; title?: string;
    optionA?: { name: string; imageUrl?: string | null; members?: string[] };
    optionB?: { name: string; imageUrl?: string | null; members?: string[] };
    closesAt?: string | null; sortOrder?: number;
  }): Promise<VgcMatch> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches/${id}`, {
      method: 'PUT', headers: this.getAuthHeaders(), body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'UPDATE_FAILED');
    return res.json();
  }

  private async vgcMatchAction(id: string, action: 'open' | 'close' | 'cancel'): Promise<VgcMatch> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches/${id}/${action}`, {
      method: 'POST', headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'ACTION_FAILED');
    return res.json();
  }
  openVgcMatch(id: string) { return this.vgcMatchAction(id, 'open'); }
  closeVgcMatch(id: string) { return this.vgcMatchAction(id, 'close'); }
  cancelVgcMatch(id: string) { return this.vgcMatchAction(id, 'cancel'); }

  async settleVgcMatch(id: string, result: VgcSide): Promise<VgcMatch> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches/${id}/settle`, {
      method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify({ result }),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'SETTLE_FAILED');
    return res.json();
  }

  async openVgcWave(wave: number): Promise<VgcMatch[]> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches/open-wave/${wave}`, {
      method: 'POST', headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error('OPEN_WAVE_FAILED');
    return res.json();
  }

  async getVgcMatchVotes(id: string): Promise<VgcMatchVotes> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/matches/${id}/votes`, { headers: this.getAuthHeaders() });
    if (!res.ok) throw new Error('VOTES_FAILED');
    return res.json();
  }

  async getVgcPredictionStats(): Promise<VgcPredictionStats> {
    const res = await fetch(`${API_BASE_URL}/manage/vgc/stats`, { headers: this.getAuthHeaders() });
    if (!res.ok) throw new Error('STATS_FAILED');
    return res.json();
  }

  // === Achievement endpoints (v7) ===

  /**
   * 미수령 도전과제 목록 조회 (peek & consume)
   * GET /api/v1/7/achievement/peek
   */
  async peekAchievements(surface?: AchievementSurface): Promise<AchievementPeekItem[]> {
    const { data } = await this.fetchAchievementPeek(surface);
    return data.items;
  }

  /**
   * 본인이 달성한 도전과제 목록 조회 (UNLOCKED only, hidden 제외)
   * GET /api/v1/7/achievement/unlocked
   */
  async getUnlockedAchievements(): Promise<UnlockedAchievementItem[]> {
    const url = `${API_BASE_URL}/7/achievement/unlocked`;
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) {
      throw new Error(`Achievement unlocked list failed: ${response.status}`);
    }
    const data = (await response.json()) as UnlockedAchievementListResponse;
    return data.items;
  }

  private async fetchAchievementPeek(surface?: AchievementSurface): Promise<{ data: AchievementPeekResponse }> {
    // 현재 화면 surface 를 넘겨야 PENDING_PAGE_VISIT 토스트가 전달된다 (peek URI 로는 surface 판별 불가).
    const url = new URL(`${API_BASE_URL}/7/achievement/peek`, window.location.origin);
    if (surface) {
      url.searchParams.set('surface', surface);
    }
    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) {
      throw new Error(`Achievement peek failed: ${response.status}`);
    }
    const data = await response.json() as AchievementPeekResponse;
    return { data };
  }

  /**
   * 도전과제 프로브 — 곡 검색 이벤트
   * POST /api/v1/7/achievement/probe/song-search
   */
  async probeSongSearch(query: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/7/achievement/probe/song-search`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ query }),
    });
    if (response.ok) {
      this.checkAchievementHeader(response);
    }
    // Probe failures are intentionally swallowed — non-critical side-effect
  }

  // === Manage / admin: achievement ===

  async listManageAchievements(): Promise<AchievementSummary[]> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to list achievements: ${response.status}`);
    return response.json();
  }

  async getManageAchievement(id: string): Promise<Achievement> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement/${encodeURIComponent(id)}`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to fetch achievement: ${response.status}`);
    return response.json();
  }

  async createManageAchievement(req: Achievement): Promise<Achievement> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(req),
    });
    if (!response.ok) throw new Error(`Failed to create achievement: ${response.status}`);
    return response.json();
  }

  async updateManageAchievement(id: string, req: Achievement): Promise<Achievement> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(req),
    });
    if (!response.ok) throw new Error(`Failed to update achievement: ${response.status}`);
    return response.json();
  }

  async disableManageAchievement(id: string): Promise<Achievement> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to disable achievement: ${response.status}`);
    return response.json();
  }

  async backfillManageAchievement(
    id: string,
    scope: 'ALL' | 'USER_IDS',
    userIds?: string[],
  ): Promise<BackfillResult> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement/${encodeURIComponent(id)}/backfill`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ scope, userIds }),
    });
    if (!response.ok) throw new Error(`Failed to backfill: ${response.status}`);
    return response.json();
  }

  async grantManageAchievement(id: string, userIds: string[]): Promise<BackfillResult> {
    const response = await fetch(`${API_BASE_URL}/manage/achievement/${encodeURIComponent(id)}/grant`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });
    if (!response.ok) throw new Error(`Failed to grant: ${response.status}`);
    return response.json();
  }

  async searchManageUsers(q: string): Promise<ManageUserSearchItem[]> {
    if (!q.trim()) return [];
    const response = await fetch(
      `${API_BASE_URL}/manage/user/search?q=${encodeURIComponent(q)}`,
      { headers: this.getAuthHeaders() },
    );
    if (!response.ok) throw new Error(`Failed to search users: ${response.status}`);
    return response.json();
  }

  async listManageAchievementProgress(
    id: string,
    page = 0,
    size = 20,
    status?: 'IN_PROGRESS' | 'UNLOCKED',
  ): Promise<ProgressPage> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(size));
    if (status) params.set('status', status);
    const response = await fetch(
      `${API_BASE_URL}/manage/achievement/${encodeURIComponent(id)}/progress?${params.toString()}`,
      { headers: this.getAuthHeaders() },
    );
    if (!response.ok) throw new Error(`Failed to fetch progress: ${response.status}`);
    return response.json();
  }

  // === Manage / admin: event outbox ===

  async listManageOutbox(status?: OutboxStatus, page = 0, size = 50): Promise<OutboxPage> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(size));
    if (status) params.set('status', status);
    const response = await fetch(
      `${API_BASE_URL}/manage/event-outbox?${params.toString()}`,
      { headers: this.getAuthHeaders() },
    );
    if (!response.ok) throw new Error(`Failed to list outbox: ${response.status}`);
    return response.json();
  }

  async retryManageOutbox(id: string): Promise<OutboxEntry> {
    const response = await fetch(
      `${API_BASE_URL}/manage/event-outbox/${encodeURIComponent(id)}/retry`,
      { method: 'POST', headers: this.getAuthHeaders() },
    );
    if (!response.ok) throw new Error(`Failed to retry outbox entry: ${response.status}`);
    return response.json();
  }

  async dismissManageOutbox(id: string): Promise<OutboxEntry> {
    const response = await fetch(
      `${API_BASE_URL}/manage/event-outbox/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST', headers: this.getAuthHeaders() },
    );
    if (!response.ok) throw new Error(`Failed to dismiss outbox entry: ${response.status}`);
    return response.json();
  }

  async getWatchedUsers(): Promise<MonitorWatchedUser[]> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/watched`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  async getWatchedUserList(): Promise<MonitorWatchedUserListItem[]> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/watched/list`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  async getPendingMonitorInvites(): Promise<MonitorWatchedUser[]> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/invites/pending`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  async inviteWatchedUser(identifier: string): Promise<void> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/invite`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ identifier }),
    });
    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
  }

  async acceptMonitorInvite(monitorId: string): Promise<void> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/invite/accept`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ monitorId }),
    });
    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
  }

  async removeWatchedUser(userId: string): Promise<void> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/watched/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  }

  // 대회 모니터링 - 특정 유저의 기록 목록 조회 (v7 전용)
  async getMonitorUserRecords(userId: string, params: MonitorUserRecordsParams): Promise<RecordListResponse> {
    const baseUrl = getVersionedBaseUrl('7');
    const url = new URL(`${baseUrl}/monitor/users/${encodeURIComponent(userId)}/records`, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value.toString());
      }
    });
    const response = await fetch(url.toString(), { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  // 대회 모니터링 - 곡별 그룹 순위 조회 (v7 전용)
  async getMonitorSongRanking(songId: number, difficulty: string): Promise<MonitorSongRanking> {
    const baseUrl = getVersionedBaseUrl('7');
    const url = new URL(`${baseUrl}/monitor/songs/${songId}/ranking`, window.location.origin);
    url.searchParams.append('difficulty', difficulty);
    const response = await fetch(url.toString(), { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  // 대회 모니터링 - 무기곡 조회 (v7 전용)
  async getMonitorWeaponSongs(userId: string, mode: string, levelLow: number, levelHigh: number, releaseVersion?: string): Promise<MonitorWeaponSong[]> {
    const baseUrl = getVersionedBaseUrl('7');
    const url = new URL(`${baseUrl}/monitor/users/${encodeURIComponent(userId)}/weapon`, window.location.origin);
    url.searchParams.append('mode', mode);
    url.searchParams.append('levelLow', levelLow.toString());
    url.searchParams.append('levelHigh', levelHigh.toString());
    if (releaseVersion) url.searchParams.append('releaseVersion', releaseVersion);
    const response = await fetch(url.toString(), { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  // 대회 모니터링 - 볼포스 표 조회 (v7 전용)
  async getMonitorVolforceTable(userId: string, recordType: string, calcVersion?: string | null): Promise<VolforceApiResponse> {
    const baseUrl = getVersionedBaseUrl('7');
    const url = new URL(`${baseUrl}/monitor/users/${encodeURIComponent(userId)}/volforce`, window.location.origin);
    url.searchParams.append('recordType', recordType);
    if (calcVersion) url.searchParams.append('calcVersion', calcVersion);
    const response = await fetch(url.toString(), { headers: this.getAuthHeaders() });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  // 대회 모니터링 - 볼포스 시뮬레이션 (v7 전용)
  async simulateMonitorVolforce(body: MonitorSimulationRequest): Promise<MonitorSimulation> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/monitor/volforce/simulate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  // ===== 리딤코드 =====

  /**
   * 리딤코드 사용 (v7 전용)
   * POST /api/v1/7/redeem
   */
  async redeemCode(code: string): Promise<RedeemResult> {
    const baseUrl = getVersionedBaseUrl('7');
    const response = await fetch(`${baseUrl}/redeem`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ code }),
    });
    this.checkAchievementHeader(response);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  // ===== 리딤코드 관리자 =====

  async getRedeemCodes(): Promise<AdminRedeemCode[]> {
    const res = await fetch(`${API_BASE_URL}/manage/redeem/codes`, { headers: this.getAuthHeaders() });
    if (!res.ok) throw new Error('LIST_FAILED');
    return res.json();
  }

  async createRedeemCode(req: UpsertRedeemCodeRequest): Promise<AdminRedeemCode> {
    const res = await fetch(`${API_BASE_URL}/manage/redeem/codes`, {
      method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'CREATE_FAILED');
    return res.json();
  }

  async updateRedeemCode(code: string, req: UpsertRedeemCodeRequest): Promise<AdminRedeemCode> {
    const res = await fetch(`${API_BASE_URL}/manage/redeem/codes/${encodeURIComponent(code)}`, {
      method: 'PUT', headers: this.getAuthHeaders(), body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'UPDATE_FAILED');
    return res.json();
  }

  async setRedeemCodeEnabled(code: string, enabled: boolean): Promise<AdminRedeemCode> {
    const action = enabled ? 'enable' : 'disable';
    const res = await fetch(`${API_BASE_URL}/manage/redeem/codes/${encodeURIComponent(code)}/${action}`, {
      method: 'POST', headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).code || 'ACTION_FAILED');
    return res.json();
  }

  async deleteRedeemCode(code: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/manage/redeem/codes/${encodeURIComponent(code)}`, {
      method: 'DELETE', headers: this.getAuthHeaders(),
    });
    if (!res.ok) throw new Error('DELETE_FAILED');
  }
}

export interface ChartPercentileResponse {
  eligible: boolean;
  topPercent: number | null;
  tiedPercent: number | null;
  bucketRatios: number[];
  myBucketIdx: number | null;
  mode: 'SCORE' | 'EX_SCORE';
}

export interface WeaponSongItem {
  songId: number;
  songName: string;
  artist: string;
  imageUrl: string;
  diff: string;
  level: number;
  topPercent: number;
  tiedPercent: number;
  myScore: number;
  deleted: boolean;
}

export interface WeaponSongsResponse {
  items: WeaponSongItem[];
}

export interface MonitorWatchedUser {
  userId: string;
  displayName: string;
  volforce: number | null;
  playCount: number | null;
  skillLevel: string | null;
  lastUpdateDate: string | null;
  profileAvatarId: string | null;
  isKonasuteUser: boolean;
}

export interface MonitorWatchedUserListItem {
  userId: string;
  displayName: string;
}

export interface MonitorSongRankingRow {
  rank: number;
  userId: string;
  displayName: string;
  score: number | null;
  exScore: number | null;
  clearType: string | null;
  volforce: number | null;
}

export interface MonitorSongRanking {
  songId: number;
  songName: string;
  artist: string;
  difficulty: string;
  level: number;
  imageUrl: string | null;
  maxExScore: number | null;
  rows: MonitorSongRankingRow[];
}

export interface MonitorWeaponSong {
  songId: number;
  songName: string;
  artist: string;
  difficulty: string;
  level: number;
  imageUrl: string | null;
  myScore: number;
  serverRank: number;
  serverTotal: number;
  sitePercent: number | null;
  releaseVersion: string | null;
}

export interface MonitorSimulationRow {
  userId: string;
  displayName: string;
  currentVf: number;
  projectedVf: number;
  delta: number;
}

export interface MonitorSimulation {
  rows: MonitorSimulationRow[];
}

export interface MonitorSimulationRequest {
  recordType: string;
  fromClearType: string;
  toClearType: string;
  calcVersion?: string | null;
}

export interface MonitorUserRecordsParams {
  levelRangeLow: number;
  levelRangeHigh: number;
  sortBy: string;
  sortOrder: string;
  recordType: string;
  gradeFilter: number;
  clearRateFilter: number;
  targetClass?: string;
  targetDate?: string;
  tagId?: string | null;
  excludeDeletedSongs?: boolean;
}

export const apiService = new ApiService();